import React, { useState } from 'react';

/**
 * Detect if running inside Safari on iOS.
 * Returns false for Chrome (CriOS), Firefox (FxiOS), in-app browsers, etc.
 */
function isIOSSafari() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (!isIOS) return false;
  // Chrome on iOS
  if (/CriOS/i.test(ua)) return false;
  // Firefox on iOS
  if (/FxiOS/i.test(ua)) return false;
  // Edge on iOS
  if (/EdgiOS/i.test(ua)) return false;
  // Opera on iOS
  if (/OPiOS/i.test(ua)) return false;
  // In-app browsers (Facebook, Instagram, Twitter, etc.)
  if (/FBAN|FBAV|Instagram|Twitter|Line\//i.test(ua)) return false;
  // If we're here, it's likely Safari
  return /Safari/i.test(ua);
}

/**
 * iOS "Add to Home Screen" instructions modal.
 * - If in Safari: shows the 3-step share instructions
 * - If NOT in Safari: shows "Open in Safari first" with copy-link button
 */
export default function IOSInstallInstructions({ onDismiss }) {
  const inSafari = isIOSSafari();
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      data-testid="ios-install-instructions">
      <div className="w-full max-w-md rounded-2xl p-6"
        style={{ backgroundColor: '#2a2a2a', animation: 'slideUp 0.3s ease-out' }}>
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>

        {!inSafari ? (
          /* ── NOT in Safari ── */
          <>
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'rgba(251,146,60,0.15)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fb923c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="text-lg font-bold mb-2 text-center" style={{ color: '#e8e3db' }}>
              Open in Safari first
            </h3>
            <p className="text-sm text-center mb-5" style={{ color: '#888' }}>
              iPhone only allows app installation from <strong style={{ color: '#e8e3db' }}>Safari</strong>. Copy this link and paste it in Safari to continue.
            </p>
            <button onClick={copyLink}
              className="w-full text-sm font-semibold py-3 rounded-xl mb-3 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#f0b90b', color: '#1a1a1a' }}
              data-testid="ios-copy-link-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
            <button onClick={onDismiss}
              className="w-full text-sm py-2 rounded-xl"
              style={{ color: '#666' }}
              data-testid="ios-install-got-it">
              Cancel
            </button>
          </>
        ) : (
          /* ── In Safari — show step-by-step instructions ── */
          <>
            <h3 className="text-lg font-bold mb-5 text-center" style={{ color: '#e8e3db' }}>
              Add TipMe to your Home Screen
            </h3>

            <div className="space-y-4 mb-6">
              {/* Step 1 */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                  style={{ backgroundColor: 'rgba(240,185,11,0.2)', color: '#f0b90b' }}>1</div>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#e8e3db' }}>
                    Tap the <span style={{ color: '#f0b90b' }}>Share</span> button below
                  </p>
                  <div className="mt-1 flex items-center gap-1">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f0b90b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                    <span className="text-xs" style={{ color: '#888' }}>in Safari's bottom toolbar</span>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                  style={{ backgroundColor: 'rgba(240,185,11,0.2)', color: '#f0b90b' }}>2</div>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#e8e3db' }}>
                    Scroll down and tap <span style={{ color: '#f0b90b' }}>Add to Home Screen</span>
                  </p>
                  <div className="mt-1 flex items-center gap-1">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f0b90b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <line x1="12" y1="8" x2="12" y2="16" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                    <span className="text-xs" style={{ color: '#888' }}>look for the + icon</span>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                  style={{ backgroundColor: 'rgba(240,185,11,0.2)', color: '#f0b90b' }}>3</div>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#e8e3db' }}>
                    Tap <span style={{ color: '#f0b90b' }}>Add</span> in the top right
                  </p>
                  <span className="text-xs" style={{ color: '#888' }}>TipMe will appear on your home screen</span>
                </div>
              </div>
            </div>

            <button onClick={onDismiss}
              className="w-full text-sm font-semibold py-3 rounded-xl"
              style={{ backgroundColor: '#f0b90b', color: '#1a1a1a' }}
              data-testid="ios-install-got-it">
              I'll do this now
            </button>
          </>
        )}
      </div>
    </div>
  );
}
